/**
 * AI Story Studio — shared demo data.
 *
 * IMPORTANT (demo honesty):
 * - No AI model is connected. SAMPLE_AI_OUTPUT is clearly labelled sample output,
 *   never presented as a real model response.
 * - Nothing here is written to the Backend. All state lives in the component only.
 */

export type StudioForm = {
  headline: string;
  author: string;
  category: string;
  summary: string;
  body: string;
  image: string;
  seoTitle: string;
  metaDescription: string;
  slug: string;
};

export const FEATURE_IMAGE =
  'https://readdy.ai/api/search-image?query=Sunlit%20golf%20course%20fairway%20with%20flags%20and%20morning%20light%2C%20junior%20golf%20players%20walking%20in%20the%20distance%2C%20deep%20green%20editorial%20sports%20photography%2C%20clean%20composition%2C%20soft%20natural%20shadows%2C%20no%20text&width=1600&height=900&seq=gs-story-studio-feature&orientation=landscape';

export const EMPTY_FORM: StudioForm = {
  headline: '',
  author: '',
  category: '',
  summary: '',
  body: '',
  image: FEATURE_IMAGE,
  seoTitle: '',
  metaDescription: '',
  slug: '',
};

export const SAMPLE_AI_OUTPUT: StudioForm = {
  headline: 'Long Beach Junior Open Expands to 36 Holes After Record Entries',
  author: 'Marisol Vega',
  category: 'Tournament Coverage',
  summary:
    'A record 214 junior entries pushed the Long Beach Junior Open to a two-day, 36-hole format for 2026 — its largest field and longest format yet.',
  body: `EL DORADO PARK — What began as a quiet Sunday-morning tradition has outgrown its bracket. With 214 junior golfers on the entry list, the Long Beach Junior Open will run two full days for the first time in its nineteen-year history, closing with a 36-hole final at El Dorado Park Golf Course.

Tournament director Ray Ortiz said the expansion wasn't a choice so much as a response. "We had to turn families away last year for the first time," he said. "Adding the second round lets us keep everyone who qualified on the course instead of on a waitlist."

The new format keeps the traditional 18-hole stroke-play qualifier on Saturday, then advances the top half of the field to a Sunday final. Organizers also added a 14-and-under flight and a separate club-division bracket, both aimed at players who are new to tournament golf.

Proceeds again benefit the Long Beach Youth Sports Fund, which has covered entry fees for more than 400 local juniors over the past decade. Sponsorship packages for the expanded weekend are open now through the GreenSide News partners desk.`,
  image: FEATURE_IMAGE,
  seoTitle: 'Long Beach Junior Open Expands to 36 Holes | GreenSide News',
  metaDescription:
    'A record 214 junior entries pushed the Long Beach Junior Open to a two-day, 36-hole format for 2026. Here is what changes, and how local businesses can sponsor it.',
  slug: 'long-beach-junior-open-expands-to-36-holes',
};

export const SAMPLE_IMAGE_PLACEMENT =
  'Feature image in the article header. A second inline photo is suggested after the second paragraph (optional in production).';

/** Date used to render the byline in the live preview. */
export const SAMPLE_PUBLISHED_DATE = '2026-10-09';

export type FormatSupport = {
  file: string;
  ext: string;
  note: string;
  supportedInPrototype: boolean;
};

/**
 * Honest format support map. Only pasted text is actually wired up in this
 * prototype; file formats are listed but explicitly NOT processed here.
 */
export const SUPPORTED_FORMATS: FormatSupport[] = [
  { file: 'Microsoft Word', ext: '.docx', note: 'Document processing available in production version', supportedInPrototype: false },
  { file: 'PDF', ext: '.pdf', note: 'Document processing available in production version', supportedInPrototype: false },
  { file: 'Plain text', ext: '.txt', note: 'Document processing available in production version', supportedInPrototype: false },
  { file: 'Pasted text', ext: 'paste', note: 'Supported in this prototype — populates the editor', supportedInPrototype: true },
];

export const STAGES = ['Uploaded', 'AI Prepared', 'Editorial Review', 'Ready to Publish', 'Published'] as const;